import { BASE_URL } from "../config/api";
import { formatVerifyFailureMessage, NO_TOKEN_FOR_VERIFY } from "./authErrors";
import {
  clearAuthSession,
  getAccessToken,
  getIdToken,
  isAuthenticated as hasActiveSession,
} from "./authSessionService";

export type OktaVerifiedUser = {
  readonly sub: string;
  /** Application user id (UUID) from POST /auth/okta/verify after login. */
  readonly id: string;
  readonly email?: string | null;
  readonly name?: string | null;
  readonly expires_at?: string | null;
  readonly avatar_url?: string | null;
};

export type EffectiveAccess = {
  readonly schema_version: number;
  readonly can_access_contracts: boolean;
  readonly can_access_rfp: boolean;
  readonly can_access_prize_rules: boolean;
  readonly decision_role_code: string | null;
  readonly can_view_all_business_units: boolean;
  readonly business_unit_codes: readonly string[];
  readonly admin_roles: readonly string[];
};

export type OktaVerifyPayload = {
  readonly valid: boolean;
  readonly user: OktaVerifiedUser;
  /** AD/Okta group ids or names from the token `groups` claim (for RBAC mapping). */
  readonly groups: readonly string[];
  readonly effective_access: EffectiveAccess;
};

type SuccessEnvelope<T> = {
  readonly success: boolean;
  readonly data: T;
  readonly message?: string;
};

function parseFastApiDetail(raw: string): string {
  try {
    const body = JSON.parse(raw) as { detail?: unknown };
    const d = body.detail;
    if (typeof d === "string" && d.trim()) return d;
    if (Array.isArray(d) && d.length > 0) return JSON.stringify(d);
  } catch {
    // Keep raw string fallback.
  }
  return raw.trim();
}

async function postVerify(bearerToken: string): Promise<SuccessEnvelope<OktaVerifyPayload>> {
  const res = await fetch(`${BASE_URL}/auth/okta/verify`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${bearerToken}`,
    },
  });
  const text = await res.text();
  if (!res.ok) {
    const detail = parseFastApiDetail(text);
    throw new Error(formatVerifyFailureMessage(res.status, detail));
  }
  return JSON.parse(text) as SuccessEnvelope<OktaVerifyPayload>;
}

export async function verifyOktaWithBackend(
  accessToken: string,
  idToken: string
): Promise<OktaVerifyPayload> {
  if (accessToken) {
    try {
      const r = await postVerify(accessToken);
      return r.data;
    } catch {
      // Fallback to ID token verification.
    }
  }
  if (!idToken) {
    throw new Error(NO_TOKEN_FOR_VERIFY);
  }
  const r = await postVerify(idToken);
  return r.data;
}

export function clearSessionAndRedirectToLogin(): void {
  clearAuthSession();
  // MemoryRouter does not re-render when tokens disappear; reload remounts so RequireAuth can send users to login.
  window.location.reload();
}

async function postLogout(): Promise<void> {
  const token = getAccessToken() ?? getIdToken();
  const headers: HeadersInit = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${BASE_URL}/auth/logout`, { method: "POST", headers });
  if (!res.ok && res.status !== 204) {
    /* best-effort */
  }
}

/** Clears local session after best-effort server logout. Caller should navigate (e.g. to `/login`) under MemoryRouter. */
export async function logout(): Promise<void> {
  try {
    await postLogout();
  } finally {
    clearAuthSession();
  }
}

export function isAuthenticated(): boolean {
  return hasActiveSession();
}
